import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../Redux/Store';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CardMedia,
  FormControl,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ImageNotSupported as ImageNotSupportedIcon,
} from '@mui/icons-material';
import { formatPrice } from '../../Formatter/formatPrice';

interface QuantitySelectionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (menuOptionId: string | null) => void;
  selectedItem: {
    id: string;
    name: string;
    price: number;
    imageUrl: string;
    menuOptionGroupId: string | null;
  } | null;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  cloudFrontUrl: string;
}

const QuantitySelectionModal: React.FC<QuantitySelectionModalProps> = ({
  open,
  onClose,
  onConfirm,
  selectedItem,
  quantity,
  onQuantityChange,
  cloudFrontUrl,
}) => {
  const menuOptions = useSelector((state: RootState) => state.order.menuOptions);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);

  // 選択中のメニューに紐づくオプショングループを取得
  const itemGroups = useMemo(() => {
    if (!selectedItem?.menuOptionGroupId || !menuOptions?.menuOptionGroups) {
      return [];
    }

    return menuOptions.menuOptionGroups.filter(group => group.menuId === selectedItem.id);
  }, [selectedItem, menuOptions]);

  // 選択中のメニューのオプションを取得
  const itemOptions = useMemo(() => {
    const options = itemGroups
      .flatMap(group => group.menuOptionGroup.menuOptions)
      .filter((option, index, self) =>
        index === self.findIndex(o => o.menuOptionId === option.menuOptionId)
      );

    return options;
  }, [itemGroups]);

  // モーダルが開かれたときにオプションの選択状態をリセット
  useEffect(() => {
    if (open) {
      setSelectedOptionId(null);
    }
  }, [open]);

  const handleConfirm = () => {
    onConfirm(selectedOptionId);
    handleClose();
  };

  const handleClose = () => {
    setSelectedOptionId(null); // リセット
    onClose();
  };

  // オプション選択時の価格調整
  const getAdjustedPrice = () => {
    if (!selectedItem) return 0;
    let basePrice = selectedItem.price;

    if (selectedOptionId) {
      const selectedOption = itemOptions.find(opt => opt.menuOptionId === selectedOptionId);
      if (selectedOption?.price) {
        if (selectedOption.priceType === 'ADDITIONAL') {
          basePrice += selectedOption.price;
        } else if (selectedOption.priceType === 'DISCOUNT') {
          basePrice -= selectedOption.price;
        }
      }
    }

    return basePrice;
  };

  if (!selectedItem) return null;

  // オプションが必須かどうかを判定
  // required === false のグループのみで構成される場合は任意（未選択でもカートに追加可）。
  // required 欠落（BE 未対応レスポンス）時は従来どおり必須扱い。
  const isOptionRequired =
    itemOptions.length > 0 &&
    itemGroups.some(group => group.menuOptionGroup.required !== false);
  // オプションが必須で未選択の場合は無効
  const isDisabled = isOptionRequired && !selectedOptionId;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          '& .MuiDialogTitle-root': {
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 1
          }
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          {selectedItem?.name}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          bgcolor: 'grey.50',
          borderRadius: 1,
          p: 2
        }}>
          {selectedItem?.imageUrl ? (
            <CardMedia
              component="img"
              sx={{
                width: 200,
                height: 200,
                objectFit: 'contain',
              }}
              image={`${cloudFrontUrl}${selectedItem.imageUrl}`}
              alt={selectedItem.name}
            />
          ) : (
            <Box
              sx={{
                width: 200,
                height: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.disabledBackground',
                borderRadius: 1,
              }}
            >
              <ImageNotSupportedIcon sx={{ fontSize: 48, color: 'action.disabled' }} />
            </Box>
          )}
        </Box>

        {itemOptions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
              {isOptionRequired
                ? 'オプションを選択してください'
                : 'オプションを選択してください（任意）'}
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1.5
            }}>
              {/* 任意グループでは「選択しない」も選択肢として表示する */}
              {!isOptionRequired && (
                <Button
                  variant={selectedOptionId === null ? "contained" : "outlined"}
                  onClick={() => setSelectedOptionId(null)}
                  sx={{
                    height: '100%',
                    minHeight: 64,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: selectedOptionId === null ? 'primary.main' : 'action.hover'
                    }
                  }}
                >
                  <Typography variant="body1">選択しない</Typography>
                </Button>
              )}
              {itemOptions.map((option) => (
                <Button
                  key={option.menuOptionId}
                  variant={selectedOptionId === option.menuOptionId ? "contained" : "outlined"}
                  onClick={() =>
                    // 任意グループでは選択済みをもう一度タップすると解除できる
                    setSelectedOptionId(
                      !isOptionRequired && selectedOptionId === option.menuOptionId
                        ? null
                        : option.menuOptionId
                    )
                  }
                  sx={{ 
                    height: '100%',
                    minHeight: 64,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: selectedOptionId === option.menuOptionId ? 'primary.main' : 'action.hover'
                    }
                  }}
                >
                  <Typography variant="body1">{option.menuOptionName}</Typography>
                  {option.price && (
                    <Typography 
                      variant="body2" 
                      color={selectedOptionId === option.menuOptionId ? "inherit" : "primary"}
                      sx={{ opacity: selectedOptionId === option.menuOptionId ? 0.9 : 1 }}
                    >
                      {option.priceType === 'ADDITIONAL' ? '+' : '-'}
                      {formatPrice(option.price)}円
                    </Typography>
                  )}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2,
          bgcolor: 'grey.50',
          borderRadius: 1,
          p: 2
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between'
          }}>
            <Typography variant="subtitle1">数量</Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <IconButton
                size="small"
                onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
                disabled={quantity <= 1 || isDisabled}
                sx={{ mx: 1 }}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Typography sx={{ 
                minWidth: 32, 
                textAlign: 'center',
                fontWeight: 'medium',
                color: isDisabled ? 'text.disabled' : 'inherit'
              }}>
                {quantity}
              </Typography>
              <IconButton
                size="small"
                onClick={() => onQuantityChange(quantity + 1)}
                disabled={isDisabled}
                sx={{ mx: 1 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'baseline'
          }}>
            <Typography variant="subtitle1">合計（税抜）</Typography>
            <Typography
              variant="h5"
              color={isDisabled ? 'text.disabled' : 'primary'}
              sx={{ fontWeight: 'medium' }}
            >
              {formatPrice(getAdjustedPrice() * quantity)}円
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        px: 3, 
        py: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        gap: 2
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ flex: 1 }}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isDisabled}
          sx={{ flex: 1 }}
        >
          カートに追加
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuantitySelectionModal;
